import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { toastInstance } from "./ToastConfig";

import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { getAccountsIdsAndIndexes, adjustOrder } from "@/utils/accounts/Utils";
import {
  getAccountsOrderFromCookies,
  setAccountsOrderInCookies,
} from "@/utils/cookies/Cookies";
import { filterUniqueTransactions } from "@/utils/transactions/GetTransactions";
import { getTransactionsPageNumberInString } from "@/utils/transactions/TransactionsUtils";

type AccountPagination = {
  page?: number;
  nextPage?: number;
};

export interface Account {
  id: string;
  status: string;
  availableBalance: number | string;
  currentBalance: number | string;
  assetTypeId: string;
  depositInstructions: any;
}

export default function AccountInformation() {
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const [accountsPagination, setAccountsPagination] = useState<{
    [key: string]: AccountPagination;
  }>({});
  const [isFetching, setIsFetching] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const { t } = useTranslation();
  const pageSize = 20;

  //Called in the first render
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!globalValues.currentAccountId) {
        dispatch(setForm({ accountsIsLoading: true }));
        await fetchAccounts();
      }

      if (globalValues.currentAccountId) {
        await fetchTransactions(0, pageSize, globalValues.currentAccountId);
      }

      setInitialFetchDone(true);
    };

    if (!initialFetchDone) {
      fetchInitialData();
    }
  }, [globalValues.currentAccountId]);

  //Called every 20 seconds
  useEffect(() => {
    let isMounted = true;
    let timerId: any;

    const fetchData = async () => {
      if (!globalValues.isTransactionsExporting) {
        await fetchAccounts();

        if (
          !globalValues.isTransactionsFiltered &&
          globalValues.currentMenuItem === t("home/home:option.home") &&
          globalValues.currentAccountId
        ) {
          await fetchTransactions(0, pageSize, globalValues.currentAccountId);
        }

        if (isMounted) {
          timerId = setTimeout(fetchData, 20000);
        }
      }
    };

    if (initialFetchDone) {
      timerId = setTimeout(fetchData, 20000);
    }

    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [
    initialFetchDone,
    isFetching,
    globalValues.isTransactionsFiltered,
    globalValues.currentMenuItem,
    globalValues.currentAccountId,
    globalValues.isTransactionsExporting,
    globalValues[`${globalValues.currentAccountId}TransactionsData`],
  ]);

  useEffect(() => {
    if (globalValues.accountHasChanges) {
      fetchAccounts();
    }
  }, [globalValues.accountHasChanges]);

  //Called when the user scrolls the page
  useEffect(() => {
    const callTransactions = async (
      moreTransactions: boolean,
      transactionsHasChanges: boolean,
      accountId: string
    ) => {
      if (moreTransactions && !transactionsHasChanges) {
        const currentAccountPagination = accountsPagination[accountId];

        if (
          currentAccountPagination.nextPage <= currentAccountPagination.page
        ) {
          toastInstance(
            "info",
            t("common:toast.subtitle.alltransactionsloaded")
          );
          dispatch(setForm({ moreTransactionsLoading: false }));
        } else {
          const transactionsLoaded = await fetchTransactions(
            currentAccountPagination.page + 1,
            pageSize,
            accountId
          );

          setAccountsPagination((prev) => ({
            ...prev,
            [accountId]: {
              page: currentAccountPagination.page + 1,
              nextPage: transactionsLoaded,
            },
          }));
        }
      } else if (transactionsHasChanges) {
        fetchTransactions(0, pageSize, accountId);
      }
    };

    if (!isFetching) {
      callTransactions(
        globalValues.moreTransactionsLoading,
        globalValues.transactionsHasChanges,
        globalValues.currentAccountId
      );
    }
  }, [
    isFetching,
    accountsPagination,
    globalValues.currentAccountId,
    globalValues.transactionsHasChanges,
    globalValues.moreTransactionsLoading,
    globalValues[`${globalValues.currentAccountId}TransactionsData`],
  ]);

  const fetchAccounts = async () => {
    try {
      const request = await sendRequest("GET", "/accounts", null, true);

      const filteredAccounts = (request ?? []).filter(
        (account: any | null | undefined) =>
          account && account.status !== "FROZEN" && account.status !== "CLOSED"
      );

      const currentAccountsSort = getAccountsOrderFromCookies();
      const updatedAccountsPagination = { ...accountsPagination };

      filteredAccounts.forEach((account: Account) => {
        if (typeof account.availableBalance === "string") {
          account.availableBalance = parseFloat(
            account.availableBalance.replace("$", "").replace(",", "")
          );
        }
        if (!updatedAccountsPagination[account.id]) {
          updatedAccountsPagination[account.id] = { page: 0, nextPage: 1 };
        }
      });
      setAccountsPagination(updatedAccountsPagination);

      if (currentAccountsSort && Object.keys(currentAccountsSort).length > 0) {
        const sortedAccounts = adjustOrder(
          currentAccountsSort,
          filteredAccounts
        );
        filteredAccounts.sort((a: Account, b: Account) => {
          const orderA = sortedAccounts[a.id];
          const orderB = sortedAccounts[b.id];

          if (orderA === undefined) return 1;
          if (orderB === undefined) return -1;

          return orderA - orderB;
        });

        const hasOrderChanged =
          Object.keys(sortedAccounts).length !==
            Object.keys(currentAccountsSort).length ||
          Object.entries(sortedAccounts).some(
            ([id, position]) => currentAccountsSort[id] !== position
          );

        if (hasOrderChanged) {
          setAccountsOrderInCookies(sortedAccounts);
        }
      } else {
        const accountsIdsAndIndexes =
          getAccountsIdsAndIndexes(filteredAccounts);
        setAccountsOrderInCookies(accountsIdsAndIndexes);
      }

      if (!globalValues.currentAccountId) {
        dispatch(setForm({ currentAccountId: filteredAccounts[0].id }));
        dispatch(
          setForm({ currentAccountAssetType: filteredAccounts[0].assetTypeId })
        );
      }

      dispatch(setForm({ accountsData: filteredAccounts }));
      dispatch(setForm({ accountsLastUpdate: new Date().toISOString() }));
    } catch (error: any) {
      console.log(error);
    } finally {
      dispatch(setForm({ accountHasChanges: false }));
      dispatch(setForm({ accountsIsLoading: false }));
    }
  };

  //Only used for load more transactions in existing account
  const fetchTransactions = async (
    page: number,
    pageSize: number,
    accountId: string
  ) => {
    setIsFetching(true);
    const currentAccountTransactions =
      globalValues[`${accountId}TransactionsData`];

    if (!currentAccountTransactions) {
      dispatch(setForm({ transactionsIsLoading: true }));
    }

    try {
      const request = await sendRequest(
        "get",
        "/transactions",
        {
          accountId,
          page,
          pageSize,
          order: "DESC",
        },
        true
      );

      const filter = filterUniqueTransactions(
        request?.transactions ?? [],
        currentAccountTransactions ?? []
      );

      const transactionsSortedByDate = filter.sort((a, b) => {
        return (
          new Date(b.transactionDate || b.createdAt).getTime() -
          new Date(a.transactionDate || a.createdAt).getTime()
        );
      });

      dispatch(
        setForm({
          transactionsData: [
            ...request?.scheduledTransactions,
            ...transactionsSortedByDate,
          ],
          [`${accountId}TransactionsData`]: [...transactionsSortedByDate],
        })
      );

      const requestNextPage =
        getTransactionsPageNumberInString(request.links?.next) ?? page;

      return requestNextPage;
    } catch (error: any) {
      if (
        error?.data?.errors &&
        Array.isArray(error.data?.errors) &&
        error?.data?.errors[0]?.description ===
          "Requested page number exceeds the actual page count"
      ) {
        console.log(error);
      } else {
        dispatch(setForm({ transactionsData: [] }));
      }
    } finally {
      dispatch(setForm({ transactionsHasChanges: false }));
      dispatch(setForm({ transactionsIsLoading: false }));
      dispatch(setForm({ moreTransactionsLoading: false }));
      setIsFetching(false);
    }
  };
  return <></>;
}
